import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { del, get, keys, set } from 'idb-keyval';
import { requisitar } from '../api/cliente';
import { useSessao } from '../sessao/sessao';
import { adaptarEsquema } from './adaptador';
import type { EsquemaRotina } from './tipos';

const prefixo = 'persona-esquema:';

interface EntradaCache {
  hash: string;
  bruto: unknown;
  /** Usuário e empresa que gravaram: o esquema traz as permissões de quem pediu, então só serve na hora para a mesma pessoa. */
  dono?: string;
}

async function doCache(codigo: string): Promise<EntradaCache | undefined> {
  try {
    return await get(prefixo + codigo);
  } catch {
    return undefined;
  }
}

async function guardar(codigo: string, entrada: EntradaCache) {
  try {
    await set(prefixo + codigo, entrada);
  } catch { /* sem IndexedDB: só a memória do TanStack Query */ }
}

async function confirmarNoServidor(codigo: string, cache: EntradaCache | undefined, dono: string | undefined, sinal?: AbortSignal): Promise<EsquemaRotina> {
  const r = await requisitar<unknown>(`/dicionario/rotinas/${encodeURIComponent(codigo)}/esquema`, {
    sinal,
    cabecalhos: cache ? { 'If-None-Match': `"${cache.hash}"` } : undefined,
  });
  if (r.resposta.status === 304 && cache) {
    if (dono && cache.dono !== dono) await guardar(codigo, { ...cache, dono });
    return adaptarEsquema(cache.bruto);
  }
  const esquema = adaptarEsquema(r.dados);
  await guardar(codigo, { hash: esquema.hash, bruto: r.dados, dono });
  return esquema;
}

export interface OpcoesEsquema {
  /** `usuario|empresa` da sessão; sem ele não serve do cache antes de o servidor confirmar. */
  dono?: string;
  /** Recebe o esquema novo quando o servidor responde diferente do que foi servido do cache. */
  aoAtualizar?: (esquema: EsquemaRotina) => void;
  /** A conferência em segundo plano falhou (rotina tirada, permissão retirada): quem chamou refaz a busca sem o cache. */
  aoFalhar?: () => void;
}

/**
 * Esquema da rotina com stale-while-revalidate (07 §6.2 item 4): o IndexedDB serve na hora — economiza uma ida e volta ao
 * servidor a cada rotina aberta — e o servidor confirma em seguida com If-None-Match. Se mudou, a tela recebe o novo;
 * se falhou, o cache sai e a busca refaz direto no servidor. Permissão é sempre conferida de novo no servidor a cada ação.
 * `dicionarioAlterado` (SignalR) invalida a query.
 */
export async function buscarEsquema(codigo: string, sinal?: AbortSignal, opcoes: OpcoesEsquema = {}): Promise<EsquemaRotina> {
  const cache = await doCache(codigo);
  const { dono, aoAtualizar, aoFalhar } = opcoes;
  if (cache && dono && cache.dono === dono) {
    // Sem o sinal da query: a conferência continua mesmo se a pessoa sair da tela, para o cache ficar certo na próxima vez.
    confirmarNoServidor(codigo, cache, dono).then(
      (novo) => { if (novo.hash !== cache.hash) aoAtualizar?.(novo); },
      async () => {
        try { await del(prefixo + codigo); } catch { /* nada a limpar */ }
        aoFalhar?.();
      },
    );
    return adaptarEsquema(cache.bruto);
  }
  return confirmarNoServidor(codigo, cache, dono, sinal);
}
export async function esquecerEsquemas(): Promise<void> {
  // Chaves por rotina: o índice das abertas fica na memória; limpar tudo é barato (poucos KB por esquema).
  try {
    for (const k of await keys()) if (String(k).startsWith(prefixo)) await del(k);
  } catch { /* nada a limpar */ }
}

/** `usuario|empresa` da sessão atual: chave de dono do cache de esquemas. */
export function useDonoEsquema(): string | undefined {
  const { sessao, empresa } = useSessao();
  return sessao ? `${sessao.usuario.id}|${empresa?.codigo ?? ''}` : undefined;
}

/** Busca do esquema ligada ao cache do TanStack Query: a página e o pré-carregamento do menu usam a mesma. */
export function consultaEsquema(cliente: QueryClient, codigo: string, dono: string | undefined) {
  const chave = ['esquema', codigo] as const;
  return {
    queryKey: chave,
    queryFn: ({ signal }: { signal?: AbortSignal }) => buscarEsquema(codigo, signal, {
      dono,
      aoAtualizar: (novo) => cliente.setQueryData(chave, novo),
      aoFalhar: () => void cliente.invalidateQueries({ queryKey: chave }),
    }),
    staleTime: 5 * 60_000,
  };
}

export function useEsquema(codigo: string) {
  const cliente = useQueryClient();
  const dono = useDonoEsquema();
  return useQuery({ ...consultaEsquema(cliente, codigo, dono), placeholderData: undefined });
}
